/** Visual tokens lifted from the dashboard design reference — shared across dashboard pages so cards/badges/type read as one system. */

import type { CSSProperties } from "react";

export const CARD_STYLE: CSSProperties = {
    backgroundColor: "var(--dc-card-bg)",
    border: "1px solid var(--dc-card-border)",
    borderRadius: "14px",
};

/** For cards made of `.dc-row` rows, which bring their own padding. */
export const CARD_NO_PADDING: CSSProperties = {
    ...CARD_STYLE,
    padding: 0,
};

/** The accent-bordered, top-lit card the reference uses to mark the selected plan. */
export const HIGHLIGHT_CARD_STYLE: CSSProperties = {
    border: "1px solid var(--blue-9)",
    borderRadius: "16px",
    background: "linear-gradient(180deg, var(--dc-accent-soft), transparent 55%), var(--dc-card-bg)",
    boxShadow: "var(--dc-shadow)",
};

export const PLAN_CARD_STYLE: CSSProperties = {
    ...CARD_STYLE,
    borderRadius: "16px",
};

/** Left-lit banner used for the usage upsell strip. */
export const BANNER_STYLE: CSSProperties = {
    border: "1px solid var(--dc-card-border)",
    borderRadius: "12px",
    background: "linear-gradient(90deg, var(--dc-accent-soft), transparent 70%), var(--dc-card-bg)",
};

export const PANEL_STYLE: CSSProperties = {
    backgroundColor: "var(--dc-panel-bg)",
    border: "1px solid var(--dc-card-border)",
    borderRadius: "12px",
};

export const MONO_STYLE: CSSProperties = {
    fontFamily: "var(--dc-font-mono)",
};

export const STAT_STYLE: CSSProperties = {
    fontFamily: "var(--dc-font-heading)",
    fontWeight: 600,
    letterSpacing: "-0.02em",
};

export const PRICE_STYLE: CSSProperties = {
    fontFamily: "var(--dc-font-heading)",
    fontWeight: 600,
    letterSpacing: "-0.03em",
};

/** Shared pill geometry — every badge on the dashboard is this shape, only color/weight vary. */
const BADGE_BASE_STYLE: CSSProperties = {
    display: "inline-block",
    padding: "4px 9px",
    borderRadius: "6px",
    fontSize: "11.5px",
};

const STATUS_BADGE_COLOR_VAR: Record<"success" | "warning" | "neutral", string> = {
    success: "var(--dc-success)",
    warning: "var(--dc-amber)",
    neutral: "var(--dc-muted)",
};

export function statusBadgeStyle(color: "success" | "warning" | "neutral"): CSSProperties {
    const colorVar = STATUS_BADGE_COLOR_VAR[color];
    return {
        ...BADGE_BASE_STYLE,
        fontWeight: 500,
        color: colorVar,
        backgroundColor: color === "neutral" ? "var(--dc-raise-bg)" : `color-mix(in srgb, ${colorVar} 16%, transparent)`,
    };
}

/** "Current plan" pill — always the vivid accent so the active plan is unmistakable. */
export const CURRENT_PLAN_BADGE_STYLE: CSSProperties = {
    ...BADGE_BASE_STYLE,
    fontWeight: 600,
    backgroundColor: "var(--blue-9)",
    color: "var(--dc-on-accent)",
};
